import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";

// ─── Icons ────────────────────────────────────────────────────────────────────
const Icons = {
  success: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 6L9 17l-5-5" />
    </svg>
  ),
  error: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  warning: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),
  info: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  ),
  close: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
};

// ─── Theme config per type ────────────────────────────────────────────────────
const THEMES = {
  success: {
    icon: Icons.success,
    accent: "#22c55e",
    bg: "rgba(20, 40, 30, 0.97)",
    border: "rgba(34, 197, 94, 0.35)",
    iconBg: "rgba(34, 197, 94, 0.15)",
    progress: "#22c55e",
    label: "Success",
  },
  error: {
    icon: Icons.error,
    accent: "#ef4444",
    bg: "rgba(40, 18, 18, 0.97)",
    border: "rgba(239, 68, 68, 0.35)",
    iconBg: "rgba(239, 68, 68, 0.15)",
    progress: "#ef4444",
    label: "Error",
  },
  warning: {
    icon: Icons.warning,
    accent: "#f59e0b",
    bg: "rgba(40, 32, 10, 0.97)",
    border: "rgba(245, 158, 11, 0.35)",
    iconBg: "rgba(245, 158, 11, 0.15)",
    progress: "#f59e0b",
    label: "Warning",
  },
  info: {
    icon: Icons.info,
    accent: "#3b82f6",
    bg: "rgba(12, 25, 48, 0.97)",
    border: "rgba(59, 130, 246, 0.35)",
    iconBg: "rgba(59, 130, 246, 0.15)",
    progress: "#3b82f6",
    label: "Info",
  },
};

// ─── Default duration ─────────────────────────────────────────────────────────
const DEFAULT_DURATION = 4000;

// ─── Context ──────────────────────────────────────────────────────────────────
const ToastContext = createContext(null);

// ─── Single Toast Item ────────────────────────────────────────────────────────
const ToastItem = ({ toast, onRemove }) => {
  const theme = THEMES[toast.type] || THEMES.info;
  const [progress, setProgress] = useState(100);
  //const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);
  const intervalRef = useRef(null);
  const duration = toast.duration ?? DEFAULT_DURATION;

  // Mount animation
  useEffect(() => {
    requestAnimationFrame(() => true//setVisible(true)
    );
  }, []);

  const handleDismiss = useCallback(() => {
    if (exiting) return;
    setExiting(true);
    //setVisible(false);
    setTimeout(() => onRemove(toast.id), 380);
  },[exiting, onRemove, toast.id]);

  // Progress bar countdown
  useEffect(() => {
    const step = 100 / (duration / 50);
    intervalRef.current = setInterval(() => {
      setProgress((p) => {
        if (p <= 0) {
          clearInterval(intervalRef.current);
          handleDismiss();
          return 0;
        }
        return p - step;
      });
    }, 50);
    return () => clearInterval(intervalRef.current);
  }, [duration, handleDismiss]);


  return (
    <>
      <style>{`
        @keyframes toast-slide-in {
          from { transform: translateX(110%); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
        @keyframes toast-slide-out {
          from { transform: translateX(0);    opacity: 1; max-height: 120px; margin-bottom: 12px; }
          to   { transform: translateX(110%); opacity: 0; max-height: 0;     margin-bottom: 0;  }
        }
        @keyframes icon-pop {
          0%   { transform: scale(0.4) rotate(-15deg); opacity: 0; }
          70%  { transform: scale(1.2) rotate(5deg);  opacity: 1; }
          100% { transform: scale(1)   rotate(0deg);  opacity: 1; }
        }
        .toast-item {
          animation: toast-slide-in 0.38s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }
        .toast-item.exiting {
          animation: toast-slide-out 0.38s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        }
        .toast-icon-pop {
          animation: icon-pop 0.45s cubic-bezier(0.34, 1.56, 0.64, 1) 0.1s both;
        }
      `}</style>

      <div
        className={`toast-item${exiting ? " exiting" : ""}`}
        style={{
          display: "flex",
          flexDirection: "column",
          width: "340px",
          borderRadius: "14px",
          overflow: "hidden",
          background: theme.bg,
          border: `1px solid ${theme.border}`,
          boxShadow: `0 8px 32px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.04), inset 0 1px 0 rgba(255,255,255,0.06)`,
          backdropFilter: "blur(20px)",
          marginBottom: "12px",
          position: "relative",
          cursor: "default",
        }}
      >
        {/* Main content row */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: "12px", padding: "14px 14px 12px 14px" }}>
          {/* Icon */}
          <div
            className="toast-icon-pop"
            style={{
              flexShrink: 0,
              width: "36px",
              height: "36px",
              borderRadius: "10px",
              background: theme.iconBg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: theme.accent,
              border: `1px solid ${theme.border}`,
            }}
          >
            <span style={{ width: "18px", height: "18px", display: "flex" }}>{theme.icon}</span>
          </div>

          {/* Text */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{
              margin: "0 0 2px 0",
              fontSize: "11px",
              fontWeight: "700",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: theme.accent,
              fontFamily: "monospace",
            }}>
              {theme.label}
            </p>
            <p style={{
              margin: 0,
              fontSize: "13.5px",
              color: "rgba(220, 230, 255, 0.9)",
              lineHeight: "1.45",
              fontFamily: "'Segoe UI', system-ui, sans-serif",
              fontWeight: "450",
              wordBreak: "break-word",
            }}>
              {toast.message}
            </p>
          </div>

          {/* Close */}
          <button
            onClick={handleDismiss}
            style={{
              flexShrink: 0,
              width: "26px",
              height: "26px",
              borderRadius: "7px",
              border: "1px solid rgba(255,255,255,0.1)",
              background: "rgba(255,255,255,0.05)",
              color: "rgba(180, 200, 255, 0.6)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.18s",
              padding: 0,
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = "rgba(255,255,255,0.12)";
              e.currentTarget.style.color = "rgba(220, 230, 255, 0.95)";
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = "rgba(255,255,255,0.05)";
              e.currentTarget.style.color = "rgba(180, 200, 255, 0.6)";
            }}
          >
            <span style={{ width: "13px", height: "13px", display: "flex" }}>{Icons.close}</span>
          </button>
        </div>

        {/* Progress bar */}
        <div style={{ height: "3px", background: "rgba(255,255,255,0.06)", position: "relative" }}>
          <div
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              height: "100%",
              width: `${progress}%`,
              background: `linear-gradient(90deg, ${theme.progress}88, ${theme.progress})`,
              transition: "width 0.05s linear",
              borderRadius: "0 2px 2px 0",
              boxShadow: `0 0 8px ${theme.progress}66`,
            }}
          />
        </div>
      </div>
    </>
  );
};

// ─── Toast Container (renders all toasts) ─────────────────────────────────────
const ToastContainer = ({ toasts, removeToast }) => (
  <div
    style={{
      position: "fixed",
      top: "20px",
      right: "20px",
      zIndex: 99999,
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-end",
      pointerEvents: "none",
    }}
  >
    {toasts.map((t) => (
      <div key={t.id} style={{ pointerEvents: "auto" }}>
        <ToastItem toast={t} onRemove={removeToast} />
      </div>
    ))}
  </div>
);

// ─── Provider ─────────────────────────────────────────────────────────────────
export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback(({ type = "info", message, duration = DEFAULT_DURATION }) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setToasts((prev) => [...prev, { id, type, message, duration }]);
  }, []);

  const toast = {
    success: (message, opts = {}) => addToast({ type: "success", message, ...opts }),
    error:   (message, opts = {}) => addToast({ type: "error",   message, ...opts }),
    warning: (message, opts = {}) => addToast({ type: "warning", message, ...opts }),
    info:    (message, opts = {}) => addToast({ type: "info",    message, ...opts }),
  };

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  );
};

// ─── Hook ─────────────────────────────────────────────────────────────────────
export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within a <ToastProvider>");
  return ctx;
};

export default ToastProvider;
